import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { hrms } from '../../services/api';
import { theme } from '../../theme';

const PERIODS = [
  { key: 'daily',   label: 'Daily' },
  { key: 'weekly',  label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly',  label: 'Yearly' },
];

const PALETTE = [
  '#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6',
  '#14B8A6','#F97316','#6366F1','#EC4899','#6B7280',
];

const fmt = (n) => Number(n).toFixed(1);

export default function TimesheetAdmin() {
  const [period, setPeriod]       = useState('weekly');
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');
  const [empFilter, setEmpFilter] = useState('');
  const [projFilter, setProjFilter] = useState('');
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [projects, setProjects]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [notSubmitted, setNotSubmitted] = useState(null);
  const [notSubmittedModal, setNotSubmittedModal] = useState(false);

  // load projects, employees, and not-submitted count on mount
  useEffect(() => {
    hrms.getProjects().then(r => setProjects(r.data || [])).catch(() => {});
    hrms.getEmployees({ limit: 500 }).then(r => {
      const d = r.data;
      setEmployees(Array.isArray(d) ? d : d.items || d.results || []);
    }).catch(() => {});
    hrms.getTimesheetNotSubmittedPrevWeek()
      .then(r => setNotSubmitted(r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { period };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      if (projFilter) params.project_id = projFilter;
      const res = await hrms.getAdminTimesheet(params);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load timesheet data.');
    } finally {
      setLoading(false);
    }
  }, [period, dateFrom, dateTo, projFilter]);

  useEffect(() => { load(); }, [load]);

  // client-side employee name filter
  const entries = data?.entries?.filter(e =>
    !empFilter ||
    e.employee_name.toLowerCase().includes(empFilter.toLowerCase()) ||
    e.emp_code.toLowerCase().includes(empFilter.toLowerCase())
  ) || [];

  const searchedEntries = entries.filter(e =>
    !search ||
    e.employee_name.toLowerCase().includes(search.toLowerCase()) ||
    (e.project_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.tender_ref   || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.description  || '').toLowerCase().includes(search.toLowerCase())
  );

  // recompute summary from filtered entries
  const filteredHours = searchedEntries.reduce((s, r) => s + (r.hours || 0), 0);

  // Recompute chart aggregates from filtered entries when any filter is active
  const isFiltered = !!(empFilter || search);

  const chartByProject = (() => {
    if (!isFiltered) return data?.by_project || [];
    const map = {};
    searchedEntries.forEach(e => {
      const key = e.project_name || e.tender_ref || 'Other';
      map[key] = (map[key] || 0) + (e.hours || 0);
    });
    return Object.entries(map)
      .map(([name, hours]) => ({ name, hours: Math.round(hours * 10) / 10 }))
      .sort((a, b) => b.hours - a.hours);
  })();

  const chartByEmployee = (() => {
    if (!isFiltered) return data?.by_employee || [];
    const map = {};
    searchedEntries.forEach(e => {
      const key = e.employee_name || 'Unknown';
      map[key] = (map[key] || 0) + (e.hours || 0);
    });
    return Object.entries(map)
      .map(([name, hours]) => ({ name, hours: Math.round(hours * 10) / 10 }))
      .sort((a, b) => b.hours - a.hours);
  })();

  const btn = (active) => ({
    padding: '0.4rem 1.1rem',
    borderRadius: '999px',
    fontSize: '0.82rem',
    fontWeight: 600,
    border: `1.5px solid ${active ? theme.primary : theme.border}`,
    background: active ? theme.primary : '#fff',
    color: active ? '#fff' : theme.textMuted,
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  const inputStyle = {
    padding: '0.5rem 0.85rem',
    border: `1.5px solid ${theme.border}`,
    borderRadius: '0.5rem',
    fontSize: '0.85rem',
    color: theme.text,
    background: '#fff',
    outline: 'none',
  };

  const card = {
    background: '#fff',
    borderRadius: '0.75rem',
    border: `1px solid ${theme.border}`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    padding: '1.25rem 1.5rem',
  };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'Inter', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: theme.text, margin: 0 }}>🕐 Timesheet Overview</h2>
          <p style={{ color: theme.textMuted, fontSize: '0.82rem', margin: '0.2rem 0 0' }}>
            {data ? `${data.date_from} → ${data.date_to} · ${data.total_entries} entries · ${fmt(data.total_hours)}h total` : 'Loading…'}
          </p>
        </div>
        <button onClick={load} style={{ ...btn(false), padding: '0.45rem 1.1rem' }}>↻ Refresh</button>
      </div>

      {/* Not Submitted Previous Week Card */}
      <div
        onClick={() => setNotSubmittedModal(true)}
        style={{ background: '#FFF7ED', border: '1.5px solid #FED7AA', borderRadius: '0.75rem', padding: '1rem 1.5rem', cursor: 'pointer', display: 'inline-flex', flexDirection: 'column', gap: '0.2rem', alignSelf: 'flex-start' }}
      >
        <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 600, color: '#9A3412', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Timesheet Not Submitted</p>
        <p style={{ margin: 0, fontSize: '0.7rem', color: '#C2410C' }}>Previous Week</p>
        <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#EA580C', lineHeight: 1 }}>
          {notSubmitted ? notSubmitted.count : '—'}
        </p>
      </div>

      {/* Period tabs + filters */}
      <div style={{ ...card, padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
          {/* Period buttons */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {PERIODS.map(p => (
              <button key={p.key} onClick={() => { setPeriod(p.key); setDateFrom(''); setDateTo(''); }}
                style={btn(period === p.key && !dateFrom)}>{p.label}</button>
            ))}
          </div>

          {/* Custom date range */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: 600 }}>Custom:</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ ...inputStyle, width: 130 }} />
            <span style={{ color: theme.textMuted, fontSize: '0.8rem' }}>→</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ ...inputStyle, width: 130 }} />
            {dateFrom && dateTo && (
              <button onClick={load} style={{ ...btn(true), padding: '0.45rem 0.9rem' }}>Apply</button>
            )}
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ ...btn(false), padding: '0.45rem 0.75rem' }}>✕</button>
            )}
          </div>

          {/* Project filter */}
          <select value={projFilter} onChange={e => setProjFilter(e.target.value)}
            style={{ ...inputStyle, minWidth: 160 }}>
            <option value="">All Projects</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          {/* Employee type-search */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              list="ts-emp-list"
              placeholder="Search employee…"
              value={empFilter}
              onChange={e => setEmpFilter(e.target.value)}
              style={{ ...inputStyle, minWidth: 200 }}
            />
            <datalist id="ts-emp-list">
              {employees.map(e => (
                <option key={e.id} value={e.full_name}>{e.emp_code} — {e.full_name}</option>
              ))}
            </datalist>
            {empFilter && (
              <button
                onClick={() => setEmpFilter('')}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: '0.9rem', lineHeight: 1 }}
              >✕</button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '0.5rem', padding: '0.75rem 1rem', color: '#DC2626', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
          <div style={{ width: 36, height: 36, border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : data && (
        <>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem' }}>
            {[
              { label: 'Total Hours',    value: `${fmt(filteredHours)}h`,       color: '#3B82F6' },
              { label: 'Entries',        value: searchedEntries.length,          color: '#10B981' },
              { label: 'Employees',      value: new Set(searchedEntries.map(e => e.employee_id)).size, color: '#F97316' },
              { label: 'Projects',       value: new Set(searchedEntries.filter(e => e.project_id).map(e => e.project_id)).size, color: '#8B5CF6' },
            ].map((k, i) => (
              <div key={i} style={{ ...card, padding: '1rem 1.25rem' }}>
                <p style={{ fontSize: '0.7rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.3rem' }}>{k.label}</p>
                <p style={{ fontSize: '1.75rem', fontWeight: 800, color: k.color, margin: 0, lineHeight: 1 }}>{k.value}</p>
                <div style={{ height: 3, background: `linear-gradient(90deg, ${k.color}, transparent)`, borderRadius: 9999, marginTop: '0.5rem' }} />
              </div>
            ))}
          </div>

          {/* Charts */}
          {(chartByProject.length > 0 || chartByEmployee.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
              {/* Hours by project */}
              <div style={card}>
                <p style={{ fontSize: '0.82rem', fontWeight: 700, color: theme.text, margin: '0 0 1rem' }}>
                  Hours by Project / Tender
                  {isFiltered && <span style={{ fontSize: '0.72rem', color: theme.primary, fontWeight: 500, marginLeft: '0.5rem' }}>(filtered)</span>}
                </p>
                {chartByProject.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartByProject.slice(0, 12)} margin={{ top: 4, right: 8, bottom: 40, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: theme.textMuted }} angle={-25} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 11, fill: theme.textMuted }} />
                      <Tooltip formatter={(v) => [`${v}h`, 'Hours']} contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
                      <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
                        {chartByProject.slice(0, 12).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p style={{ color: theme.textMuted, fontSize: '0.82rem', textAlign: 'center', padding: '2rem 0' }}>No data</p>}
              </div>

              {/* Hours by employee */}
              <div style={card}>
                <p style={{ fontSize: '0.82rem', fontWeight: 700, color: theme.text, margin: '0 0 1rem' }}>
                  Hours by Employee (Top 12)
                  {isFiltered && <span style={{ fontSize: '0.72rem', color: theme.primary, fontWeight: 500, marginLeft: '0.5rem' }}>(filtered)</span>}
                </p>
                {chartByEmployee.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartByEmployee.slice(0, 12)} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 70 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: theme.textMuted }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: theme.textMuted }} width={70} />
                      <Tooltip formatter={(v) => [`${v}h`, 'Hours']} contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
                      <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
                        {chartByEmployee.slice(0, 12).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p style={{ color: theme.textMuted, fontSize: '0.82rem', textAlign: 'center', padding: '2rem 0' }}>No data</p>}
              </div>
            </div>
          )}

          {/* Entries table */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <p style={{ fontSize: '0.88rem', fontWeight: 700, color: theme.text, margin: 0 }}>
                Timesheet Entries ({searchedEntries.length})
              </p>
              <input
                type="text"
                placeholder="Search entries…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ ...inputStyle, width: 220 }}
              />
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    {['Date', 'Employee', 'Dept', 'Type', 'Project / Tender', 'Description', 'Hours'].map(h => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 700, color: theme.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {searchedEntries.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.85rem' }}>
                        No timesheet entries found for this period.
                      </td>
                    </tr>
                  ) : searchedEntries.map((e, i) => (
                    <tr key={e.id} style={{ borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap', color: theme.textMuted }}>{e.date}</td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        <div style={{ fontWeight: 600, color: theme.text }}>{e.employee_name}</div>
                        <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{e.emp_code}</div>
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>{e.department}</td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        <span style={{
                          padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700,
                          background: e.row_type === 'tender' ? '#FEF3C7' : '#EFF6FF',
                          color:      e.row_type === 'tender' ? '#92400E'  : '#1D4ED8',
                        }}>
                          {e.row_type === 'tender' ? '🏷 Tender' : '📂 Project'}
                        </span>
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        {e.row_type === 'tender' ? (
                          <div>
                            <div style={{ fontWeight: 600, color: theme.text }}>{e.tender_title || e.tender_ref || '—'}</div>
                            {e.tender_ref && <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{e.tender_ref}</div>}
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontWeight: 600, color: theme.text }}>{e.project_name || '—'}</div>
                            {e.project_code && <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{e.project_code}</div>}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.description || '—'}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 700, color: '#3B82F6', whiteSpace: 'nowrap' }}>
                        {fmt(e.hours)}h
                      </td>
                    </tr>
                  ))}
                </tbody>
                {searchedEntries.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#F0F9FF', borderTop: `2px solid ${theme.border}` }}>
                      <td colSpan={6} style={{ padding: '0.65rem 0.75rem', fontWeight: 700, color: theme.text, fontSize: '0.82rem' }}>
                        Total ({searchedEntries.length} entries)
                      </td>
                      <td style={{ padding: '0.65rem 0.75rem', fontWeight: 800, color: '#3B82F6', fontSize: '0.9rem' }}>
                        {fmt(filteredHours)}h
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
      {/* Not Submitted Modal */}
      {notSubmittedModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', width: '100%', maxWidth: '640px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Timesheet Not Submitted — Previous Week</h2>
                {notSubmitted && <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6B7280' }}>{notSubmitted.week_start} → {notSubmitted.week_end}</p>}
              </div>
              <button onClick={() => setNotSubmittedModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6B7280' }}>&times;</button>
            </div>
            {!notSubmitted ? (
              <p style={{ textAlign: 'center', color: '#6B7280', padding: '2rem 0' }}>Loading...</p>
            ) : notSubmitted ? (
              <>
                <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#6B7280' }}>{notSubmitted.count} employee(s) have not submitted timesheet for the previous week.</p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB' }}>
                      {['Emp Code', 'Name', 'Department'].map(h => (
                        <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.72rem', color: '#6B7280', borderBottom: '1px solid #E5E7EB', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {notSubmitted.employees.map(e => (
                      <tr key={e.employee_id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600, color: '#4338CA' }}>{e.emp_code}</td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>{e.full_name}</td>
                        <td style={{ padding: '0.6rem 0.75rem', color: '#6B7280' }}>{e.department || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : <p style={{ textAlign: 'center', color: '#6B7280', padding: '2rem 0' }}>Failed to load data.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
